import { useCreateFormStore } from '@components/form';
import { createForm } from '@components/form/create-form';
import { hideModal, Modal, useModal } from '@components/modal';
import { codeMap } from '@components/phone-field/country-data';
import { Stack } from '@components/stack';
import { useScopedTranslate } from '@locale/translate';
import { getKeys } from '@utils/objects';

import { useCurrentCompany } from 'api/authentication/hooks';
import { useUpdateBillingInfo } from 'api/company/hooks';
import { BillingAddress } from 'api/company/requests';

export const useEditBillingInfo = () => useModal(EditBillingInfo, { variant: 'LARGE' });

const Form = createForm<BillingAddress>();

export const EditBillingInfo = ({ billingAddress }: { billingAddress?: BillingAddress }) => {
  const { t } = useScopedTranslate(locale);
  const company = useCurrentCompany();
  const formStore = useCreateFormStore();
  const editMutation = useUpdateBillingInfo();

  const countryOptions = getKeys(codeMap).map((key) => ({
    label: codeMap[key],
    value: key,
  }));

  const handleSubmit = async (data: BillingAddress) => {
    const billingData = {
      ...data,
      firstName: billingAddress?.firstName ?? '',
      lastName: billingAddress?.lastName ?? '',
    };
    await editMutation.mutateAsync(billingData);
    hideModal();
  };

  return (
    <Modal>
      <Modal.SimpleHeader title={t('invoiceInfo')} />
      <Form store={formStore} onSubmit={handleSubmit}>
        <Modal.Content>
          <Stack space={4}>
            <Form.TextField
              id="company"
              label={t('companyName')}
              defaultValue={billingAddress?.company}
            />
            <Form.TextField id="email" label={t('email')} defaultValue={billingAddress?.email} />
            <Form.TextField id="phone" label={t('phone')} defaultValue={billingAddress?.phone} />
            {company?.isPaydayCustomer ? (
              <Form.TextField
                id="country"
                label={t('country')}
                defaultValue={billingAddress?.country}
              />
            ) : (
              <Form.Select
                id="country"
                label={t('country')}
                defaultValue={billingAddress?.country}
                options={countryOptions}
              />
            )}
            <Stack space={8} orientation="horizontal" align="center">
              <div style={{ flex: 1 }}>
                <Form.TextField
                  id="line1"
                  label={t('line1')}
                  defaultValue={billingAddress?.line1}
                />
              </div>
              {!company?.isPaydayCustomer && (
                <div style={{ flex: 1 }}>
                  <Form.TextField
                    id="line2"
                    label={t('line2')}
                    defaultValue={billingAddress?.line2}
                  />
                </div>
              )}
            </Stack>
            <Stack space={8} orientation="horizontal" align="center">
              <div style={{ flex: 1 }}>
                <Form.TextField id="city" label={t('city')} defaultValue={billingAddress?.city} />
              </div>
              <div style={{ flex: 1 }}>
                <Form.TextField id="zip" label={t('zip')} defaultValue={billingAddress?.zip} />
              </div>
            </Stack>
            {!company?.isPaydayCustomer && (
              <div style={{ flex: 1 }}>
                <Form.TextField
                  id="state"
                  label={t('state')}
                  defaultValue={billingAddress?.state}
                />
              </div>
            )}
          </Stack>
        </Modal.Content>
        <Modal.Footer>
          <Form.Actions>
            <Form.Reset text={t('core.cancel')} variant="secondary" onClick={hideModal} />
            <Form.Submit text={t('core.save')} variant="primary-blue" disabled={false} />
          </Form.Actions>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export const locale = {
  en: {
    invoiceInfo: 'Invoice information',
    companyName: 'Company name',
    line1: 'Address line 1',
    line2: 'Address line 2',
    zip: 'Postal/Zip code',
    city: 'City',
    country: 'Country',
    taxId: 'Tax ID',
    taxIDPlaceholder: 'Tax ID (ex. VAT or GST)',
    invoiceEmail: 'Invoice email',
    forInvoices: 'For invoices',
    email: 'Email',
    phone: 'Phone',
    state: 'State',
  },
};
